import { useCallback } from 'react';

import { randomUUID as uuidv4 } from 'expo-crypto';
import { useRouter } from 'expo-router';
import { useStoreContext } from 'features/store';
import { differenceBy, intersectionBy } from 'lodash-es';

import { ICombo, ILegacyCartEntry } from '@rbi-ctg/menu';
import {
  transformUserSelectionsComboToComboSlotSelections,
  transformUserSelectionsModifierToModifierSelection,
} from 'components/product-detail-v2/utils';
import { toaster } from 'components/toaster/toaster';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { useRoute } from 'hooks/navigation/use-route';
import { useCart } from 'hooks/use-cart';
import { useRedeemReward } from 'pages/cart/cart-item/hooks/use-redeem-reward-state';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { IAddToCartSelectionAttributes } from 'state/amplitude/types';
import { mapEntryTypeToOfferType } from 'state/cart/helpers';
import { useFavoritesContext } from 'state/favorites';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { RewardRedemptionMethod } from 'state/loyalty/hooks/types';
import { useGetAvailableRewards } from 'state/loyalty/hooks/use-get-available-rewards';
import { LoyaltyAppliedOffer, isLoyaltyOffer } from 'state/loyalty/types';
import { findRewardInAppliedRewards } from 'state/loyalty/utils';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useEditingCartEntry } from 'state/product-wizard-v2/hooks/use-editing-cart-entry';
import useProductWizardFlow from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { getCartEntriesBenefit } from 'utils/cart/helper';
import { HapticsImpactStyle, hapticImpact } from 'utils/haptic';
import { routes } from 'utils/routing';
import { transformMenuObjectToCartItem } from 'utils/wizard';
import { IMenuObjectToCartEntry } from 'utils/wizard/types';

export const useProductCart = () => {
  const enableRewardUnstacking = useFlag(LaunchDarklyFlag.ENABLE_REWARD_UNSTACKING);
  const { editFavorite, tempFavorite: currentFavorite } = useFavoritesContext();
  const { getAvailableRewardFromCartEntry } = useGetAvailableRewards();
  const { isOffer } = useMenuContext();
  const {
    addItemToCart,
    cartEntries,
    removeFromCart,
    repriceCartEntries,
    updateCartEntry,
  } = useCart();
  const {
    selectedProduct,
    productQuantity,
    userSelections,
    manualModifications,
  } = useProductWizardContext();

  const dispatch = useAppDispatch();
  const editingCartEntry = useEditingCartEntry();
  const { totalPrice } = useProductPrice();
  const { incentiveNotInMenu } = useRedeemReward();

  const { pathname } = useRoute();
  const { rewardBenefitId } = useProductWizardFlow();
  const router = useRouter();

  const { store } = useStoreContext();
  const distanceToStore = useDistance({
    restaurant: store,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  const shouldDisplayMenuV2Toast = !!pathname.startsWith(routes.menu);

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const selectedLoyaltyOffer = useAppSelector(selectors.loyalty.selectSelectedOffer);
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const selectedOffer = selectedLoyaltyOffer;

  // Adds multiple cart entries to the cart
  const addMultipleToCart = useCallback(
    (
      newCartEntries: ILegacyCartEntry[],
      selectionAttributes?: IAddToCartSelectionAttributes | undefined
    ) => {
      const showBulkMessage = newCartEntries.length > 1;

      newCartEntries.forEach(entry => {
        addItemToCart(entry, selectionAttributes, !showBulkMessage);
      });

      if (showBulkMessage) {
        if (shouldDisplayMenuV2Toast) {
          // Menu Screens Toast
          return toaster.addToCartToast(
            t('addToCartMultipleSuccess', {
              itemName: newCartEntries[0].name,
              quantity: newCartEntries.length - 1,
            }),
            newCartEntries[0].image
          );
        }

        toaster.positive(
          t('addToCartMultipleSuccess', {
            itemName: newCartEntries[0].name,
            quantity: newCartEntries.length - 1,
          })
        );
      }
    },
    [addItemToCart, shouldDisplayMenuV2Toast]
  );

  // Adds or Updates CartEntries
  // Determines which ones are being updated and which ones are new using their cartIds
  const upsertCart = useCallback(
    (
      upsertCartEntries: ILegacyCartEntry[],
      selectionAttributes?: IAddToCartSelectionAttributes | undefined
    ) => {
      const newEntries = differenceBy(upsertCartEntries, cartEntries, 'cartId');
      if (newEntries?.length) {
        addMultipleToCart(newEntries, selectionAttributes);
      }

      const existingEntries = intersectionBy(upsertCartEntries, cartEntries, 'cartId');
      // When cart V2 is enabled and the user increase the quantity of an item
      // we are creating a new entries with the same _id.
      // On that case we don't want to show the message when updating an existing same _id entries.
      const differentEntries = differenceBy(existingEntries, newEntries, '_id');
      const skipShowMessage = !differentEntries.length;

      existingEntries.forEach((newEntry: ILegacyCartEntry) =>
        updateCartEntry(newEntry, newEntry.cartId, skipShowMessage)
      );
    },
    [addMultipleToCart, cartEntries, updateCartEntry]
  );

  // Cart v2 let user to duplicate an item using the incrementor and apply a reward to the duplicate item. As this could happens in some places in the app not always we have the rewardBenefitId in the params.
  // So we need to check if the rewardBenefitId is available and apply the reward to the cart entry.
  // If cart V2 is disabled we will always use the rewardBenefitId from the params.
  const processRewardRedemption = useCallback(
    (cartEntry: ILegacyCartEntry) => {
      const rewardInfo = getAvailableRewardFromCartEntry(cartEntry);

      if (!rewardBenefitId && !rewardInfo?.rewardBenefitId) {
        return;
      }

      // If enableRewardUnstacking flag is enabled and cart entry is the exact same as
      // a previously added reward do not apply nor remove any reward
      if (
        appliedLoyaltyRewardsArray?.length &&
        enableRewardUnstacking &&
        appliedLoyaltyRewardsArray[0].rewardId === rewardInfo.id
      ) {
        return;
      }

      logRBIEvent({
        name: CustomEventNames.REWARD_ADDED_TO_ORDER,
        type: EventTypes.Other,
        attributes: {
          engineId: rewardInfo?.id ?? '',
          sanityId: rewardInfo?.sanityId ?? '',
          name: rewardInfo?.name ?? '',
          redemptionMode: 'Online',
          distanceToStore,
        },
      });

      if (appliedLoyaltyRewardsArray?.length && enableRewardUnstacking) {
        removeFromCart({ cartId: appliedLoyaltyRewardsArray[0].cartId });
      } else {
        const { appliedReward, limitPerOrderMet } = findRewardInAppliedRewards(
          appliedLoyaltyRewardsArray || [],
          rewardInfo?.id || '',
          rewardInfo?.limitPerOrder
        );

        if (appliedReward && limitPerOrderMet) {
          removeFromCart({ cartId: appliedReward.cartId });
        }
      }

      const rewardPayload = {
        rewardBenefitId: rewardBenefitId || rewardInfo?.rewardBenefitId || '',
        cartId: cartEntry.cartId,
        redemptionMethod: RewardRedemptionMethod.MOBILE,
        // If a loyalty incentive is not available as a regular menu entry,
        // the quantity must match rewards applied
        ...(incentiveNotInMenu && { quantity: 1 }),
        enableRewardUnstacking,
      };

      dispatch(actions.loyalty.applyReward(rewardPayload));
    },
    [
      getAvailableRewardFromCartEntry,
      rewardBenefitId,
      distanceToStore,
      appliedLoyaltyRewardsArray,
      incentiveNotInMenu,
      dispatch,
      removeFromCart,
      enableRewardUnstacking,
    ]
  );

  const handleRewardsRedemption = useCallback(
    (cartEntries: ILegacyCartEntry[]) => {
      if (!rewardBenefitId) {
        return;
      }

      const entriesRedemption = getCartEntriesBenefit(
        cartEntries,
        getAvailableRewardFromCartEntry,
        rewardBenefitId
      );

      if (entriesRedemption) {
        entriesRedemption?.forEach(processRewardRedemption);
      }
    },
    [getAvailableRewardFromCartEntry, processRewardRedemption, rewardBenefitId]
  );

  const handleOffers = useCallback(
    ({
      mainEntry,
      upsellEntries,
      modifiersInjected,
      cartEntries,
    }: IMenuObjectToCartEntry & { cartEntries: ILegacyCartEntry[] }) => {
      const offerPrice = totalPrice(modifiersInjected);
      if (selectedOffer && isLoyaltyOffer(selectedOffer)) {
        if (!selectedOffer.isStackable) {
          appliedOffers.forEach(offer => {
            if (!offer.isStackable && !!offer.cartId) {
              removeFromCart({ cartId: offer.cartId });
            }
          });
        }
        dispatch(
          actions.loyalty.applyOffer({
            id: selectedOffer.loyaltyEngineId,
            type: (selectedOffer as LoyaltyAppliedOffer).type,
            cartId: mainEntry?.cartId,
            isStackable: selectedOffer.isStackable,
            cmsId: selectedOffer._id,
          })
        );
        dispatch(actions.loyalty.setSelectedOffer(null));
        upsertCart([
          {
            ...mainEntry,
            price: offerPrice,
            // FIXME: VendorConfigs types between LoyaltyOffer and ILegacyCartEntry does not match
            // @ts-ignore
            offerVendorConfigs: selectedOffer.vendorConfigs,
            ...(mainEntry && { type: mapEntryTypeToOfferType(mainEntry.type) }),
          },
          ...upsellEntries,
        ]);
      }
    },
    [appliedOffers, dispatch, removeFromCart, selectedOffer, totalPrice, upsertCart]
  );

  const handleTransformMenuObjectToCartItem = useCallback(
    ({ isNewItem, quantity }: { isNewItem: boolean; quantity: number }) => {
      const { mainEntry, upsellEntries, modifiersInjected } = transformMenuObjectToCartItem(
        selectedProduct,
        {
          quantity,
          modifierSelections: transformUserSelectionsModifierToModifierSelection(userSelections),
          pickerSelections: userSelections.pickerAspects,
          comboSlotSelections: transformUserSelectionsComboToComboSlotSelections(
            userSelections.comboSlot,
            selectedProduct as ICombo
          ),
          pathname,
          existingCartId: editingCartEntry?.cartId,
          shouldLogEvents: !!isOffer,
        }
      );

      if (isOffer && selectedOffer) {
        handleOffers({
          mainEntry,
          upsellEntries,
          modifiersInjected,
          cartEntries,
        });
      }

      const newCartId = isNewItem ? uuidv4() : editingCartEntry?.cartId || mainEntry?.cartId;

      const cartEntriesWithoutPrice = isOffer
        ? upsellEntries
        : ([
            {
              ...mainEntry,
              cartId: newCartId,
              lineId: newCartId,
            },
            ...upsellEntries,
          ] as ILegacyCartEntry[]);

      return repriceCartEntries(cartEntriesWithoutPrice);
    },
    [
      cartEntries,
      editingCartEntry?.cartId,
      handleOffers,
      isOffer,
      pathname,
      repriceCartEntries,
      selectedOffer,
      selectedProduct,
      userSelections,
    ]
  );

  /**
   * Creates cart entries from the quantity of the product
   * @param quantity - The quantity of new items to create
   * @returns The new cart entries
   */
  const createCartEntriesFromItemQuantity = useCallback(
    (quantity: number) => {
      const cartEntries = [];
      for (let i = 0; i < quantity; i++) {
        const repricedCartEntries = handleTransformMenuObjectToCartItem({
          isNewItem: true,
          quantity: 1,
        });
        cartEntries.push(...repricedCartEntries);
      }
      return cartEntries;
    },
    [handleTransformMenuObjectToCartItem]
  );

  const addToOrder = useCallback(() => {
    let expandedCartEntries: ILegacyCartEntry[] = [];

    // If the product quantity is greater than 1, we need to create new cart entries for the same product.
    if (productQuantity > 1) {
      // We don't need to create a new cart entry for the first item because it's already in the cart.
      const quantityToCreate = productQuantity - 1;
      expandedCartEntries = createCartEntriesFromItemQuantity(quantityToCreate);
    }

    if (currentFavorite) {
      const cartEntries = handleTransformMenuObjectToCartItem({
        isNewItem: false,
        quantity: productQuantity,
      });

      return editFavorite({ cartEntries });
    }

    const currentCartEntry = handleTransformMenuObjectToCartItem({
      isNewItem: false,
      quantity: 1,
    });

    const finalItems = [...currentCartEntry, ...expandedCartEntries];

    if (finalItems.length) {
      const selections: IAddToCartSelectionAttributes = {
        itemModified: manualModifications.modifiers,
        comboSlotSelection: manualModifications.comboSlot,
        pickerAspectSelection: manualModifications.pickerAspects,
      };
      upsertCart(finalItems, selections);
    }

    const isEditingRewardItem = !!rewardBenefitId;
    const currentCartEntryHasRewardApplied =
      currentCartEntry?.[0] &&
      appliedLoyaltyRewardsArray?.some(reward => reward.cartId === currentCartEntry[0].cartId);

    // If current cart entry has a reward applied, apply the rewards to the expanded cart entries too
    if (currentCartEntryHasRewardApplied) {
      handleRewardsRedemption(expandedCartEntries);
    }
    // If we are editing a reward item and none are currently applied, apply the rewards to the final items
    else if (isEditingRewardItem) {
      handleRewardsRedemption(finalItems);
    }

    hapticImpact({ style: HapticsImpactStyle.Heavy });
    return editingCartEntry ? router.dismiss() : router.replace(routes.menu);
  }, [
    productQuantity,
    handleTransformMenuObjectToCartItem,
    currentFavorite,
    rewardBenefitId,
    appliedLoyaltyRewardsArray,
    editingCartEntry,
    router,
    createCartEntriesFromItemQuantity,
    editFavorite,
    manualModifications.modifiers,
    manualModifications.comboSlot,
    manualModifications.pickerAspects,
    upsertCart,
    handleRewardsRedemption,
  ]);

  return {
    addToOrder,
  };
};
